import { strandedHeldCount, type StrandedStatusPalette, type UpgradeDriftReport } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";

// The update notice's third kind of news, after data that's cleared and fields that stop showing: statuses on this
// dial that the new version's palettes don't have. A version can keep a check, or the dial-wide palette, and change its
// status keys; the dial keeps the old keys, so nothing is deleted, but whatever holds one shows no status afterwards.

// KEY-721: the action-point row reads the dial's own word (KEY-539), so it is filled in per call.
const holderNoun = (holder: StrandedStatusPalette["holder"], noun: SegmentNoun): [one: string, many: string] =>
  holder === "Item" ? ["item", "items"] : holder === "Segment" ? [noun.one, noun.many] : ["section", "sections"];

/** What a palette whose statuses don't carry over is called on the notice: the check, the action points or the sections. */
export function strandedPaletteTitle(p: StrandedStatusPalette, noun: SegmentNoun = DEFAULT_SEGMENT_NOUN): string {
  switch (p.holder) {
    case "Item":
      return p.label || p.paletteRef;
    case "Segment":
      return p.paletteRef === "default" || !p.label ? `${noun.One} statuses` : `${p.label}'s own statuses`;
    default:
      return "Section statuses";
  }
}

/** The old statuses on this palette that something on the dial still holds, with how many hold each. */
export function describeStrandedPalette(p: StrandedStatusPalette, noun: SegmentNoun = DEFAULT_SEGMENT_NOUN): string {
  const [one, many] = holderNoun(p.holder, noun);
  return p.keys
    .filter((k) => k.statusCount + k.revertCount > 0)
    .map((k) => {
      const held = k.statusCount > 0 ? ` on ${k.statusCount.toLocaleString("en-GB")} ${k.statusCount === 1 ? one : many}` : "";
      const reverts = k.revertCount > 0
        ? ` (${held ? "and " : ""}${k.revertCount.toLocaleString("en-GB")} review ${k.revertCount === 1 ? "date" : "dates"} set to change back to it)`
        : "";
      return `${k.oldName || k.oldKey}${held}${reverts}`;
    })
    .join("; ");
}

/** The notice's box listing statuses the new version doesn't have. Renders nothing when nothing on the dial holds one. */
export function StrandedStatusesNotice({ drift, version, noun = DEFAULT_SEGMENT_NOUN }: { drift: UpgradeDriftReport | null | undefined; version: number | string; noun?: SegmentNoun }) {
  const stranded = (drift?.strandedStatuses ?? []).filter((p) => strandedHeldCount(p) > 0);
  if (stranded.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 12, padding: "10px 12px", borderRadius: 8,
        border: "1px solid color-mix(in srgb, var(--st-progress, #b45309) 40%, var(--border))",
        background: "color-mix(in srgb, var(--st-progress, #b45309) 8%, transparent)",
      }}
    >
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--st-progress, #b45309)" }}>
        Some statuses on this dial aren't in version {version}, so anything set to one of them will show no status:
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
        {stranded.map((p) => (
          <li key={`${p.holder}-${p.paletteRef}`} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            <b style={{ color: "var(--text)" }}>{strandedPaletteTitle(p, noun)}</b> — {describeStrandedPalette(p, noun)}
          </li>
        ))}
      </ul>
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 5 }}>
        Nothing is deleted. After the update, give those one of the new version's statuses so they count towards the
        dial again.
      </div>
    </div>
  );
}
